/**
 * @author        joseph.murphy@zumper.com (murph)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   this will be deleted when backend takes over this functionality
 */

import { getAPIFriendlyFilters } from '@zcode-ui/filters'
import {
  getUser,
  onMessageSent,
  selectIsMobile,
  selectIsOnline,
} from '@zcode-ui/web-zumper-global-state'
import { ButterAction } from '@zcode-ui/web-zumper-global-state'
import type { ErrorData } from '@zcode/error-filters'
import { USER_ID_LOCAL_STORAGE_KEY } from '@zcode/integrations'
import { getGclidFromLocalStorage, getMsclkidFromLocalStorage } from '@zcode/legacy-local-storage'
import { identify } from '@zcode/mixpanel'
import { localStorageSingleton } from '@zcode/nr-utils'
import saveMessageUser from '@zcode/nr/es/shared/actions/sendMessage/helper/saveMessageUser'
import setupMessageQuery from '@zcode/nr/es/shared/actions/sendMessage/helper/setupMessageQuery'
import * as Api from '@zcode/nr/es/shared/api'
import type {
  APPLY,
  MESSAGE,
  MESSAGE_FORM_INLINE,
  MESSAGE_FORM_MODAL,
  TOUR,
  WAITLIST,
} from '@zcode/nr/es/shared/constants/message'
import { selectIsLoggedIn } from '@zcode/nr/es/shared/modules/user/selectors'
import { requeue } from '@zcode/nr/es/shared/packages/offline-middleware/actions'
import { getVisitedExperiments } from '@zcode/optimizely-client'
import type {
  DlcMessagedProperty,
  ExtraThunkArg,
  Filters,
  GetState,
  InitialMessageData,
  Listable,
  Profile,
  Property,
} from '@zcode/state-type'
import { ONE_MINUTE } from '@zcode/utils'
import { getViewCountObject } from '../../components/detail/helpers/viewCount/viewCountObject'
import { getFavorited } from '../../inspectors/index'
import { logger } from '../../logger'
import { selectFilters } from '../../modules/filters/selectors/index'
import { getPageNameFromRouteProps } from '../../modules/mixpanel/helpers/getPageNameFromRouteProps'
import { setProfile } from '../../modules/profile/actions/index'
import type { Dispatch } from '../../modules/types'
import { getMessageFavoritesPayload } from '../helpers/getMessageFavoritesPayload'
import { setupMessageData } from './setupMessageData'
import { trackSendMessageDlc } from './trackSendMessageDlc'

export interface MessagedType {
  listings: { [key: number]: number } | null
  buildings: { [key: number]: number } | null
}

export interface SendMessageAlcArgs {
  entity: Listable | Property
  selectedFloorplanId?: number | null | undefined
  equalCTAIntent?: typeof APPLY | typeof MESSAGE | typeof TOUR | typeof WAITLIST | undefined
  formType?: typeof MESSAGE_FORM_INLINE | typeof MESSAGE_FORM_MODAL
  listingIds?: readonly number[] | undefined
  buildingIds?: readonly number[] | undefined
  messageData: InitialMessageData
  oneTap?: boolean
  showButter?: boolean
}

export const sendMessageAlc =
  (sendMessageArgs: SendMessageAlcArgs) =>
  async (
    dispatch: Dispatch,
    getState: GetState,
    { optimizely }: ExtraThunkArg
  ): Promise<DlcMessagedProperty[] | undefined> => {
    const {
      entity,
      equalCTAIntent,
      formType,
      messageData: _messageData,
      oneTap = false,
      selectedFloorplanId,
      showButter,
    } = sendMessageArgs
    if (entity?.isRentpath) {
      return
    }

    const state = getState()
    const prevSearchFilters: Readonly<Filters> | undefined =
      localStorageSingleton.get('prevSearchFilters')
    const filters = prevSearchFilters ?? selectFilters(state) ?? {}
    const user = getUser(state)
    const isMobile = selectIsMobile(state)
    const isOnline = selectIsOnline(state)
    const isLoggedIn = selectIsLoggedIn(state)
    const listablesQuery = getAPIFriendlyFilters({ filters })
    const favorites = getMessageFavoritesPayload(getFavorited(state))
    const userEmail = _messageData.email ?? user.email

    const messageData = {
      ...setupMessageData({
        equalCTAIntent,
        entity,
        formType,
        messageData: {
          origin: _messageData.origin,
          moveIn: user.tenantProfile ? user.tenantProfile.moveIn : '',
          email: userEmail,
          name: _messageData.name ?? user.fullName,
          phone: _messageData.phone ?? user.phone,
        },
        isMulti: true,
        multiListables: [],
        isMobile,
      }),
      listingIds: selectedFloorplanId
        ? [Number(selectedFloorplanId)]
        : entity.listingId
          ? [entity.listingId]
          : [],
    }

    // Optimizely visited experiments
    const visitedExperiments = getVisitedExperiments(optimizely)

    let query: ReturnType<typeof setupMessageQuery>
    try {
      query = setupMessageQuery({
        config: {
          entity,
          isMobile,
          isMulti: true,
        },
        messageData,
        oneTap,
        variationMap: visitedExperiments,
        // TODO: uncomment this once https://github.com/zumper/web-zumper/pull/5698 is merged
        // variationMap: optimizley.exportVariationMap()
      })
    } catch (e) {
      logger.error('ALC messaging without property id', entity)
      return await Promise.reject(e)
    }

    if (!isOnline) {
      if (entity?.isListing) {
        const callback = async () => await dispatch(sendMessageAlc(sendMessageArgs))
        const options = { before: ONE_MINUTE }
        dispatch(requeue({ callback, options, name: 'sendMessageAlc' }))
      }
      return await Promise.reject(new Error('connection error: sendMessageAlc'))
    }

    const timestamp = ~~(Date.now() / 1000)
    const alcOrigin = `dlc-${query.message.origin}`

    query.message.email && identify(query.message.email)
    // send message and attach resolve and reject behaviors that should occur on every send.
    let response: DlcMessagedProperty[] | null = null
    const { buildings, listings }: MessagedType = localStorageSingleton?.get?.('messaged') ?? {
      listings: null,
      buildings: null,
    }
    const buildingIds = buildings ? Object.keys(buildings).map((key) => Number(key)) : []
    const listingsIds = listings ? Object.keys(listings).map((key) => Number(key)) : []
    return await Api.dlc
      .query({
        ...query.message,
        // alc messages are built in backend
        message: '',
        // rather than modify code that won't be deleted
        // I think it's better we just manually overwrite
        // origin here to have alc prefix
        messaged: {
          building_ids: buildingIds,
          listing_ids: listingsIds,
        },
        origin: alcOrigin,
        filters: listablesQuery,
        detail_views: getViewCountObject(),
        favorites,
        gclid: getGclidFromLocalStorage(localStorageSingleton),
        msclkid: getMsclkidFromLocalStorage(localStorageSingleton),
        traktor_id: localStorageSingleton.get(USER_ID_LOCAL_STORAGE_KEY),
      })
      .then(
        async (result: DlcMessagedProperty[]) => {
          response = result
          if (result.length) {
            const messagedListingIds: number[] = []
            const messagedBuildingIds: number[] = []
            for (const { buildingId, listingId } of result) {
              if (buildingId) {
                messagedBuildingIds.push(buildingId)
              }
              if (listingId) {
                messagedListingIds.push(listingId)
              }
            }
            messageData.messagedListingIds = messagedListingIds
            messageData.messagedBuildingIds = messagedBuildingIds
          }

          // saves messaged to local storage
          dispatch(
            saveMessageUser({
              config: {
                entity,
                isMulti: true,
                isLoggedIn,
              },
              equalCTAIntent,
              messageData,
              moveInText: undefined,
              setProfile: (profile: Profile) => dispatch(setProfile(profile)),
              timestamp,
              user,
            })
          )
          // add to bundle.messaged redux state
          dispatch(
            onMessageSent({
              success: true,
              buildingIds: messageData.messagedBuildingIds,
              equalCTAIntent,
              listingIds: messageData.messagedListingIds,
              timestamp,
            })
          )

          const pageName = getPageNameFromRouteProps({
            routeProps: {
              location: { pathname: window.location.pathname, search: window.location.search },
            },
            isMobile,
          })

          trackSendMessageDlc({
            entity,
            origin: alcOrigin,
            pageName,
          })

          if (showButter) {
            dispatch(ButterAction.addSuccessButterBar({ message: 'Message sent' }))
          }

          return result
        },
        async (err: ErrorData) => {
          return await Promise.reject(err)
        }
      )
      .catch(async (err: ErrorData) => {
        // collect errors in either then/err handler above
        if (response === null) {
          if (!err.status) {
            logger.error('ALC message was not sent', messageData, err)
          }
          return await Promise.reject(err)
        }
        err.status !== 450 &&
          logger.error('Error after ALC message.send success', response, messageData, err)
        return response
      })
  }
